import { DEFAULT_CURRENCY, formatCurrency, type CurrencyFormat } from '@1nl/core';

export const AUD_FORMAT: CurrencyFormat = {
  code: 'AUD',
  symbol: 'A$',
  minorUnitsPerMajor: 100,
  thousandsSeparator: ',',
  decimalSeparator: '.',
};

export const CURRENCY_FORMATS: Record<string, CurrencyFormat> = {
  PGK: DEFAULT_CURRENCY,
  AUD: AUD_FORMAT,
};

const NO_SYMBOL_FORMAT: CurrencyFormat = { ...DEFAULT_CURRENCY, symbol: '' };

/** The single source for the no-symbol fallback rendering: grouped digits/2dp
 *  (via DEFAULT_CURRENCY's separators) + a space + the raw code, never an
 *  invented symbol. Used for unknown codes here and for every non-PGK code in
 *  formatMoney (format.ts). */
export function formatWithCodeSuffix(minor: number, code: string): string {
  return `${formatCurrency(minor, NO_SYMBOL_FORMAT)} ${code}`;
}

export function formatInCurrency(minor: number, code: string): string {
  const format = CURRENCY_FORMATS[code];
  if (format) return formatCurrency(minor, format);
  return formatWithCodeSuffix(minor, code);
}

export function minorUnitsPerMajor(code: string): number {
  return CURRENCY_FORMATS[code]?.minorUnitsPerMajor ?? 100;
}

export function compactInCurrency(minor: number, code: string): string {
  const major = minor / minorUnitsPerMajor(code);
  const abs = Math.abs(major);
  let magnitude: string;
  if (abs >= 1_000_000) magnitude = `${(major / 1_000_000).toFixed(1)}M`;
  else if (abs >= 1_000) magnitude = `${(major / 1_000).toFixed(1)}K`;
  else magnitude = major.toFixed(0);
  return `${CURRENCY_FORMATS[code]?.symbol ?? ''}${magnitude}`;
}

export const DISPLAY_CURRENCIES = [
  { code: 'PGK', flag: '🇵🇬', label: 'PGK' },
  { code: 'AUD', flag: '🇦🇺', label: 'AUD' },
] as const;
